import moment from 'moment';
import type { MomentInput } from 'moment';

import {
	getMomentUtc,
	dayJsToString,
	datetimeToString,
	warningIfNotUtcMidnight,
	jsNativeDateToDayJs,
	dateToUnix,
	roundNativeDate,
	roundMoment,
	isDayjs,
} from '@/components/common/dateUtils';

import Consts from '@/api/Consts';

import type {
	AxisTimestampInput,
	ChartBaseOptions,
	ChartItem,
	ChartPointObject,
	ChartPointTuple,
	ChartSeries,
	GenerateSeriesParams,
	GenerateSeriesResult,
	MinuteAggregateItem,
	PointValue,
	SeriesKey,
	SeriesPoint,
	SeriesStepUnit,
	SubNetworkLabelItem,
} from '@/utils/types/chartsUtilsTypes';


const toMomentInput = (value: unknown): MomentInput => {
	if (value == null) {
		return '';
	}
	if (typeof value === 'string' || typeof value === 'number') {
		return value;
	}
	if (value instanceof Date) {
		return value;
	}
	return String(value);
};


const generateColors = (series: ChartSeries[], repeat: number): string[] => {
	const count = series.length;
	const colors: string[] = [];
	for (let i = 0; i < count; i++) {
		for (let j = 0; j < repeat; j++) {
			colors.push(Consts.getColor(i, Consts.CHART_COLORS));
		}
	}
	return colors;
};


const generateDashArray = (series: ChartSeries[], repeat: number): number[] => {
	const count = series.length;
	const dashArray: number[] = [];
	for (let i = 0; i < count; i++) {
		for (let j = 0; j < repeat; j++) {
			dashArray.push(Math.floor(i / Consts.CHART_COLORS.length));
		}
	}
	return dashArray;
};


const generateMarkerSizes = (series: ChartSeries[]): number[] => {
	const sizes: number[] = [];
	for (let i = 0; i < series.length; i++) {
		const seriesItem = series[i];
		if (seriesItem === undefined) {
			continue;
		}
		sizes.push((seriesItem.data.length === 1) ? 3 : 0);
	}
	return sizes;
};


const pointEntryFactoryXYDay = (date: unknown, value: PointValue): ChartPointObject => ({ x: date, y: value });
const pointEntryFactoryArrayTime = (date: unknown, value: PointValue): ChartPointTuple => [date, value];


const shouldReplacePointValue = (existing: PointValue, incoming: PointValue): boolean => {
	if (existing == null && incoming == null) {
		return false;
	}
	if (existing == null) {
		return incoming != null;
	}
	if (incoming == null) {
		return false;
	}
	return existing < incoming;
};


const generateSeries = ({
	items = [], // все записи в кучу
	datetimeField = 'current_datetime', // имя поля со временем для оси Ox
	getSeriesKeyFn = item => {
		const name = item.name;
		if (typeof name === 'string' || typeof name === 'number') {
			return name;
		}
		return String(name);
	}, // функция для получения ключа серии
	getValueFn = item => {
		const value = item.value;
		return typeof value === 'number' ? value : null;
	}, // функция для получения значения точки
	seriesStep = 1, // шаг (число)
	seriesStepUnit = 'minute', // единица измерения шага. Если day, включается особенный режим даты. Единственное число: day, minute
	categoryFormatter = dateToUnix, // функция для форматирования значения Ox
	pointEntryFactory = pointEntryFactoryArrayTime, // функция для создания записи точки в серии
	showNullAsZero = false, // подменять в данных null на 0
	minDate = null, // минимальная дата -- необязательный параметр, тип Date
	maxDate = null, // максимальная дата -- необязательный параметр, тип Date
	forceSeriesKeys = null, // массив ключей серий, которые нужно добавить даже если данных нет
	roundTimeValues = false, // если true, то значения времени округляются вниз до ближайшего значения seriesStep
	showOnlyFirstSeries = false, // если true, то показывается только первая серия
	keySorter = null, // функция для сортировки ключей серий, если не указана, то алфавитный порядок
	discontinuous = false, // X-сетка только из реально присутствующих меток, см. блок в теле функции
	// пропускает точки с null значением и соединяет через них линию.
	// Включение true может приводить к багам тултипа, но для отладки иногда надо.
	skipNullValues = false,
	// массив полей, которые нужно скопировать из объектов данных в каждую точку серии
	// может работать только с pointEntryFactoryXYDay и другими функциями, которые возвращают объект
	pushAdditionalFields = [],
}: GenerateSeriesParams = {}): GenerateSeriesResult => {
	const dayMode = seriesStepUnit === 'day';
	let minDateCalc: Date | null = minDate;
	if (minDate != null && roundTimeValues) {
		minDateCalc = roundNativeDate(minDate, seriesStepUnit, seriesStep);
	}
	let maxDateCalc: Date | null = maxDate;
	if (maxDate != null && roundTimeValues) {
		maxDateCalc = roundNativeDate(maxDate, seriesStepUnit, seriesStep);
	}
	const seriesMap: Record<string, Map<number, { value: PointValue } & Record<string, unknown>>> = {};
	for (const item of items) {
		seriesMap[String(getSeriesKeyFn(item))] = new Map();
	}
	if (forceSeriesKeys != null) {
		forceSeriesKeys.forEach(seriesKey => {
			const seriesKeyStr = String(seriesKey);
			if (!Object.prototype.hasOwnProperty.call(seriesMap, seriesKeyStr)) {
				seriesMap[seriesKeyStr] = new Map();
			}
		});
	}
	for (const item of items) {
		let startMoment = moment(toMomentInput(item[datetimeField]));
		if (dayMode) {
			startMoment = startMoment.utcOffset(0, true);
		}
		if (roundTimeValues) {
			startMoment = roundMoment(startMoment, seriesStepUnit, seriesStep);
		}
		const start = startMoment.toDate();
		if (minDate == null && (minDateCalc == null || start < minDateCalc)) {
			minDateCalc = start;
		}
		if (maxDate == null && (maxDateCalc == null || start > maxDateCalc)) {
			maxDateCalc = start;
		}
		const row = seriesMap[String(getSeriesKeyFn(item))];
		if (row === undefined) {
			continue;
		}
		const startInt = Number(start);
		const value = getValueFn(item);
		const existingPoint = row.get(startInt);
		if (!row.has(startInt) || (existingPoint !== undefined && shouldReplacePointValue(existingPoint.value, value))) {
			const pointObj: { value: PointValue } & Record<string, unknown> = { value };
			for (const field of pushAdditionalFields) {
				const fieldValue = item[field];
				pointObj[field] = fieldValue || null;
			}
			row.set(startInt, pointObj);
		}
	}
	const series: ChartSeries[] = [];
	const categories: unknown[] = [];
	const sorter = (keySorter != null) ? keySorter : (a: string, b: string) => a.localeCompare(b);
	const seriesKeys = Object.keys(seriesMap).sort(sorter);
	// X-сетку считаем один раз и переиспользуем для всех серий: одинаковые X нужны
	// для корректных общих тултипов. В режиме discontinuous берем только реально
	// присутствующие в данных метки времени вместо равномерного шага seriesStep —
	// это убирает массовое null-заполнение разреженных данных, из-за которого
	// ApexCharts рисует по <path> на каждую точку и подвешивает браузер.
	let gridDatetimes: Array<ReturnType<typeof jsNativeDateToDayJs>>;
	if (discontinuous) {
		const timestampSet = new Set<number>();
		for (const key of Object.keys(seriesMap)) {
			const mapItem = seriesMap[key];
			if (mapItem === undefined) {
				continue;
			}
			for (const timestamp of mapItem.keys()) {
				timestampSet.add(timestamp);
			}
		}
		gridDatetimes = [...timestampSet]
			.sort((a, b) => a - b)
			.map(timestamp => jsNativeDateToDayJs(new Date(timestamp)));
	} else {
		gridDatetimes = [];
		let pointDatetime = jsNativeDateToDayJs(minDateCalc);
		const end = jsNativeDateToDayJs(maxDateCalc);
		while (pointDatetime.isBefore(end) || pointDatetime.isSame(end)) {
			gridDatetimes.push(pointDatetime);
			pointDatetime = pointDatetime.add(seriesStep, seriesStepUnit);
		}
	}
	seriesKeys.forEach((seriesKey, index) => {
		const finalSeriesData: SeriesPoint[] = [];
		const seriesMapItem = seriesMap[seriesKey];
		if (seriesMapItem === undefined) {
			return;
		}
		for (const pointDatetime of gridDatetimes) {
			const pointDatetimeNumber = Number(pointDatetime);
			const storedPoint = seriesMapItem.get(pointDatetimeNumber);
			const value = seriesMapItem.has(pointDatetimeNumber) && storedPoint !== undefined
				? storedPoint.value
				: (showNullAsZero ? 0 : null);
			const category = categoryFormatter(pointDatetime);
			if (index === 0) {
				categories.push(category);
			}
			if (value === null && skipNullValues) {
				continue;
			}
			const point = pointEntryFactory(category, value);
			if (seriesMapItem.has(pointDatetimeNumber) && storedPoint !== undefined && !Array.isArray(point)) {
				for (const field of pushAdditionalFields) {
					point[field] = storedPoint[field] || null;
				}
			}
			finalSeriesData.push(point);
		}
		series.push({
			name: seriesKey,
			hidden: showOnlyFirstSeries && index > 0,
			data: finalSeriesData,
		});
	});
	return { series, categories };
};


const generateRangeSeries = (
	items: ChartItem[], // все записи в кучу
	datetimeField: string, // имя поля со временем для оси Ox
	getSeriesKeyFn: (item: ChartItem) => SeriesKey, // функция для получения ключа серии
	getValueFn: (item: ChartItem) => number, // функция для получения значения точки
	seriesStep: number, // шаг (число)
	seriesStepUnit: SeriesStepUnit, // единица измерения шага
	minDate: Date, // минимальная дата ! обязательный параметр, тип Date
	maxDate: Date, // максимальная дата ! обязательный параметр, тип Date
): ChartSeries[] => {
	const seriesMap: Record<string, Map<number, number>> = {};
	for (const item of items) {
		seriesMap[String(getSeriesKeyFn(item))] = new Map();
	}
	for (const item of items) {
		const start = getMomentUtc(toMomentInput(item[datetimeField])).toDate();
		const row = seriesMap[String(getSeriesKeyFn(item))];
		if (row !== undefined) {
			row.set(Number(start), getValueFn(item));
		}
	}
	const series: ChartSeries[] = [];
	const seriesKeys = Object.keys(seriesMap).sort();
	seriesKeys.forEach((seriesKey, index) => {
		const finalSeriesData: ChartPointObject[] = [];
		warningIfNotUtcMidnight(minDate, 'minDate', 'generateRangeSeries');
		warningIfNotUtcMidnight(maxDate, 'maxDate', 'generateRangeSeries');
		let pointDatetime = jsNativeDateToDayJs(minDate);
		const end = jsNativeDateToDayJs(maxDate);
		const seriesMapItem = seriesMap[seriesKey];
		if (seriesMapItem === undefined) {
			return;
		}
		while (pointDatetime.isBefore(end) || pointDatetime.isSame(end)) {
			const pointDatetimeNumber = Number(pointDatetime);
			const storedValue = seriesMapItem.get(pointDatetimeNumber);
			finalSeriesData.push({
				x: dayJsToString(pointDatetime),
				y: seriesMapItem.has(pointDatetimeNumber) && storedValue !== undefined ? storedValue : 0,
			});
			pointDatetime = pointDatetime.add(seriesStep, seriesStepUnit);
		}
		series.push({
			name: seriesKey,
			hidden: index > 0,
			data: finalSeriesData,
		});
	});
	return series;
};


const getBaseOptions = (chartId: string, series: ChartSeries[], colorRepeat?: number): ChartBaseOptions => {
	const repeat = colorRepeat != null ? colorRepeat : 1;
	return {
		chart: {
			id: chartId,
			animations: {
				enabled: false,
				dynamicAnimation: {
					enabled: false,
				},
			},
			toolbar: {
				show: true,
				autoSelected: 'pan',
				tools: {
					download: false,
					selection: false,
					reset: true,
				},
			},
		},
		colors: generateColors(series, repeat),
		stroke: {
			width: 2,
		},
		dataLabels: {
			enabled: false,
		},
		fill: {
			opacity: 1,
		},
		markers: {
			size: generateMarkerSizes(series),
			hover: {
				size: 5,
			},
		},
		xaxis: {
			title: {
				text: 'GPS-time',
				style: Consts.AXIS_STYLE,
			},
			type: 'datetime',
			formatter: datetimeToString,
		},
		yaxis: {
			title: {
				text: 'milliseconds',
				style: Consts.AXIS_STYLE,
			},
			min: 0,
			decimalsInFloat: 0,
		},
		tooltip: {
			x: {
				show: true,
				formatter: value => datetimeToString(new Date(value)),
			},
			y: {
				show: true,
			},
		},
		legend: {},
	};
};


const toAxisTimestamp = (value: AxisTimestampInput): number | null => {
	if (value == null) {
		return value;
	}
	if (typeof value === 'number') {
		return value;
	}
	if (value instanceof Date) {
		return value.getTime();
	}
	if (isDayjs(value)) {
		return value.valueOf();
	}
	return null;
};


const setXAxisRange = (
	options: ChartBaseOptions,
	datesRange: readonly [AxisTimestampInput, AxisTimestampInput],
): void => {
	options.xaxis.min = toAxisTimestamp(datesRange[0]);
	options.xaxis.max = toAxisTimestamp(datesRange[1]);
};


const nullSafeFormatter = (func: (value: number) => string): (value: number | null) => string => {
	return value => (value == null) ? 'N/A' : func(value);
};


const getSubNetworkLabel = (subNetworks: SubNetworkLabelItem[], subNetworkId: number): string => {
	const subNetwork = subNetworks.find(sn => sn.id === subNetworkId);
	return subNetwork != null ? subNetwork.label : 'Sub network ' + subNetworkId;
};


type MakeSubNetworkParamSeriesKeyParams = {
	subNetworks: SubNetworkLabelItem[];
	period: number;
	getParamFn: (item: ChartItem) => string;
};


// Строит ключ серии вида 'subNetwork-param' (или 'subNetwork-param-aggFunction' при period > 1).
// getParamFn задаёт, как из item получить param (например, метку маунтпойнта или перевод).
const makeSubNetworkParamSeriesKey = ({
	subNetworks,
	period,
	getParamFn,
}: MakeSubNetworkParamSeriesKeyParams) => (item: ChartItem): string => {
	const subNetworkId = item.sub_network;
	const snLabel = getSubNetworkLabel(
		subNetworks,
		typeof subNetworkId === 'number' ? subNetworkId : Number(subNetworkId),
	);
	const param = getParamFn(item);
	if (period > 1) {
		const aggFunction = item.agg_function;
		return snLabel + '-' + param + '-' + String(aggFunction);
	}
	return snLabel + '-' + param;
};


const getClosestValue = (value: number, references: readonly number[]): number => {
	const firstRef = references[0];
	if (firstRef === undefined) {
		throw new Error('getClosestValue: references must not be empty');
	}
	let closest = firstRef;
	for (const ref of references) {
		if (Math.abs(value - ref) < Math.abs(value - closest)) {
			closest = ref;
		}
	}
	return closest;
};


const calcMedian = (values: number[]): number | null => {
	if (values.length === 0) {
		return null;
	}
	const sorted = values.sort((a, b) => a - b);
	const middle = Math.floor(sorted.length / 2);
	if (sorted.length % 2 !== 0) {
		const middleValue = sorted[middle];
		return middleValue !== undefined ? middleValue : null;
	}
	const lower = sorted[middle - 1];
	const upper = sorted[middle];
	if (lower === undefined || upper === undefined) {
		return null;
	}
	return (lower + upper) / 2;
};


const calcMean = (values: number[]): number | null => {
	if (values.length === 0) {
		return null;
	}
	return values.reduce((acc, value) => acc + value, 0) / values.length;
};


const MINUTE_AGGREGATES = [5, 10, 15, 30, 60, 120, 240, 480, 720, 1440] as const;


/*
Converts list of objects such:
{
	e_coord: 90000.06335423933,
	epoch: "2025-04-07T15:29:00Z",
	n_coord: 35999.994679174604,
	u_coord: -800.0475083094142,
}
to list of objects such:
{
	epoch: "2025-04-07T15:29:00Z",
	name: "e_coord",
	value: 90000.06335423933,
},
{
	epoch: "2025-04-07T15:29:00Z",
	name: "n_coord",
	value: 35999.994679174604,
},
{
	epoch: "2025-04-07T15:29:00Z",
	name: "u_coord",
	value: -800.0475083094142,
}
*/
const splitObjectToSeries = (series: ChartItem[], xKey: string, yKeys: string[]): ChartItem[] => {
	const result: ChartItem[] = [];
	for (const item of series) {
		for (const yKey of yKeys) {
			const newItem: ChartItem = {};
			newItem[xKey] = item[xKey];
			newItem.name = yKey;
			newItem.value = item[yKey];
			result.push(newItem);
		}
	}
	return result;
};


const aggregateByMinute = (
	seriesData: MinuteAggregateItem[],
	seriesNames: string[],
): MinuteAggregateItem[] => {
	const result: MinuteAggregateItem[] = [];
	for (const seriesName of seriesNames) {
		const minuteValues: Record<string, number[]> = {};
		for (const item of seriesData.filter(item => item.name === seriesName)) {
			let epoch = moment(item.epoch);
			epoch = epoch.startOf('minute');
			const epochKey = epoch.toISOString();
			if (!minuteValues[epochKey]) {
				minuteValues[epochKey] = [];
			}
			const bucket = minuteValues[epochKey];
			if (bucket !== undefined) {
				bucket.push(item.value);
			}
		}
		for (const key of Object.keys(minuteValues)) {
			const bucket = minuteValues[key];
			if (bucket === undefined) {
				continue;
			}
			const aggregatedValue = calcMean(bucket);
			if (aggregatedValue !== null) {
				result.push({
					name: seriesName,
					epoch: key,
					value: aggregatedValue,
				});
			}
		}
	}
	return result;
};


const colorWithOpacity = (hexColor: string, opacity: number): string => {
	const hex = hexColor.replace('#', '');
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);
	return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + opacity + ')';
};


type PointAnnotationMarkerEntry = {
	x: number | string;
	y: number;
	marker: {
		size: number;
		fillColor: string;
		strokeColor: string;
		strokeWidth: number;
	};
};


const getPointValue = (point: SeriesPoint | undefined): PointValue => {
	if (point === undefined) {
		return null;
	}
	return Array.isArray(point) ? point[1] : point.y;
};


// Изолированная точка (соседи с обеих сторон null) не рисуется линией — сегмент
// строить не из чего. Глобальные маркеры на всех точках резко замедляют график,
// поэтому для таких точек отдаём точечные аннотации: их столько же, сколько самих
// изолированных точек, и число DOM-узлов не растет пропорционально всем точкам.
const buildIsolatedPointAnnotations = (
	series: ChartSeries[],
	colors: string[],
	size = 4,
): PointAnnotationMarkerEntry[] => {
	const annotations: PointAnnotationMarkerEntry[] = [];
	series.forEach((seriesItem, seriesIndex) => {
		const data = seriesItem.data;
		for (let i = 0; i < data.length; i++) {
			const point = data[i];
			const value = getPointValue(point);
			if (point === undefined || value == null) {
				continue;
			}
			const prevValue = getPointValue(i > 0 ? data[i - 1] : undefined);
			const nextValue = getPointValue(i < data.length - 1 ? data[i + 1] : undefined);
			if (prevValue == null && nextValue == null) {
				const x = Array.isArray(point) ? point[0] : point.x;
				const color = colors[seriesIndex] != null ? colors[seriesIndex] : '#000000';
				annotations.push({
					x: x as number | string,
					y: value,
					marker: {
						size,
						fillColor: color,
						strokeColor: color,
						strokeWidth: 0,
					},
				});
			}
		}
	});
	return annotations;
};


export {
	generateDashArray,
	pointEntryFactoryXYDay,
	generateSeries,
	generateRangeSeries,
	getBaseOptions,
	setXAxisRange,
	nullSafeFormatter,
	getSubNetworkLabel,
	makeSubNetworkParamSeriesKey,
	colorWithOpacity,
	buildIsolatedPointAnnotations,
	getClosestValue,
	calcMedian,
	splitObjectToSeries,
	aggregateByMinute,
	MINUTE_AGGREGATES,
};
